import { useState, useEffect, useRef } from "react";
import { useAuth, useDrive } from "../App";
import { Sidebar } from "./Dashboard";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { format } from "date-fns";
import {
  Receipt,
  Upload,
  Plus,
  Search,
  Trash2,
  Edit2,
  Eye,
  CheckCircle2,
  AlertCircle,
  CalendarIcon,
  Loader2,
  Image as ImageIcon
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Receipts() {
  const { accessToken } = useAuth();
  const { initialized, isAdmin, folderIds, uploadFile, listFiles, getFileAsJson, deleteFile, getMonthFolder, blobToBase64 } = useDrive();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [vatEnabled, setVatEnabled] = useState(false);
  const fileInputRef = useRef(null);

  // Form state for manual entry
  const [manualForm, setManualForm] = useState({
    merchantName: "",
    transactionDate: new Date(),
    totalAmount: "",
    vatAmount: "",
    vatRate: "",
    category: "",
    notes: ""
  });

  // Form state for editing
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    if (initialized && folderIds.receipts) {
      loadReceipts();
    } else if (initialized) {
      setLoading(false);
    }
    if (initialized && folderIds.config) {
      loadVatSetting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, folderIds]);

  const loadVatSetting = async () => {
    try {
      const files = await listFiles(folderIds.config);
      const configFile = files.find(f => f.name === 'config.json');
      if (configFile) {
        const config = await getFileAsJson(configFile.id);
        setVatEnabled(config.business?.vatEnabled || false);
      }
    } catch (e) {
      console.log("No config found");
    }
  };

  const loadReceipts = async () => {
    setLoading(true);
    try {
      const allReceipts = [];
      
      // List all month folders
      const monthFolders = await listFiles(folderIds.receipts, 'application/vnd.google-apps.folder');
      
      for (const folder of monthFolders) {
        const files = await listFiles(folder.id);
        const jsonFiles = files.filter(f => f.name.endsWith('.json'));
        
        for (const jsonFile of jsonFiles) {
          try {
            const receipt = await getFileAsJson(jsonFile.id);
            // Find corresponding image
            const imageName = jsonFile.name.replace('.json', '');
            const imageFile = files.find(f => f.name.startsWith(imageName) && !f.name.endsWith('.json'));
            
            allReceipts.push({
              ...receipt,
              fileId: jsonFile.id,
              imageFileId: imageFile?.id,
              monthFolder: folder.name
            });
          } catch (e) {
            console.error("Error loading receipt:", e);
          }
        }
      }
      
      // Sort by date descending
      allReceipts.sort((a, b) => new Date(b.transactionDate) - new Date(a.transactionDate));
      setReceipts(allReceipts);
    } catch (error) {
      console.error("Error loading receipts:", error);
      toast.error("Failed to load receipts");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Use JPEG, PNG, GIF, or WebP");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum 10MB");
      return;
    }

    setUploading(true);
    
    try {
      // Get current month folder
      const now = new Date();
      const yearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const monthFolderId = await getMonthFolder(folderIds.receipts, yearMonth);
      
      // Generate unique ID
      const receiptId = `receipt_${Date.now()}`;
      
      // Convert to base64 for OCR
      const base64 = await blobToBase64(file);
      const base64Data = base64.split(',')[1];
      
      // Call OCR API
      let ocrResult = {
        success: false,
        merchantName: "Unknown",
        totalAmount: 0,
        vatAmount: 0,
        transactionDate: now.toISOString()
      };
      
      try {
        const ocrResponse = await fetch(`${BACKEND_URL}/api/ocr/process`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${accessToken}`
          },
          body: JSON.stringify({ image_base64: base64Data })
        });
        
        if (ocrResponse.ok) {
          ocrResult = await ocrResponse.json();
        }
      } catch (e) {
        console.error("OCR error:", e);
      }
      
      // Upload image to Drive
      const ext = file.name.split('.').pop();
      await uploadFile(`${receiptId}.${ext}`, file, file.type, monthFolderId);
      
      // Create receipt metadata
      const receiptData = {
        id: receiptId,
        merchantName: ocrResult.merchant_name || ocrResult.merchantName || "Unknown",
        transactionDate: ocrResult.transaction_date || ocrResult.transactionDate || now.toISOString(),
        totalAmount: ocrResult.total_amount || ocrResult.totalAmount || 0,
        vatAmount: ocrResult.vat_amount || ocrResult.vatAmount || 0,
        vatRate: ocrResult.vat_rate || ocrResult.vatRate || null,
        netAmount: (ocrResult.total_amount || 0) - (ocrResult.vat_amount || 0),
        rawOcrText: ocrResult.raw_text || "",
        confidence: ocrResult.confidence || 0,
        isMatched: false,
        matchedTransactionIds: [],
        createdAt: now.toISOString()
      };
      
      // Save JSON metadata
      await uploadFile(`${receiptId}.json`, JSON.stringify(receiptData, null, 2), 'application/json', monthFolderId);
      
      if (ocrResult.success) {
        toast.success(`Receipt processed! Extracted: ${receiptData.merchantName}`);
      } else {
        toast.warning("Receipt saved but OCR unavailable. Edit details manually.");
      }
      
      setUploadDialogOpen(false);
      loadReceipts();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload receipt");
    } finally {
      setUploading(false);
    }
  };

  const handleManualCreate = async () => {
    if (!manualForm.merchantName || !manualForm.totalAmount) {
      toast.error("Merchant name and total amount are required");
      return;
    }

    setUploading(true);
    
    try {
      const now = new Date();
      const transactionDate = manualForm.transactionDate;
      const yearMonth = `${transactionDate.getFullYear()}-${String(transactionDate.getMonth() + 1).padStart(2, '0')}`;
      const monthFolderId = await getMonthFolder(folderIds.receipts, yearMonth);
      
      const receiptId = `receipt_${Date.now()}`;
      const vatRate = manualForm.vatRate && manualForm.vatRate !== "exempt" ? parseFloat(manualForm.vatRate) : null;
      
      const receiptData = {
        id: receiptId,
        merchantName: manualForm.merchantName,
        transactionDate: transactionDate.toISOString(),
        totalAmount: parseFloat(manualForm.totalAmount),
        vatAmount: manualForm.vatAmount ? parseFloat(manualForm.vatAmount) : 0,
        vatRate: vatRate,
        netAmount: parseFloat(manualForm.totalAmount) - (manualForm.vatAmount ? parseFloat(manualForm.vatAmount) : 0),
        category: manualForm.category,
        notes: manualForm.notes,
        confidence: 1.0,
        isMatched: false,
        matchedTransactionIds: [],
        createdAt: now.toISOString()
      };
      
      await uploadFile(`${receiptId}.json`, JSON.stringify(receiptData, null, 2), 'application/json', monthFolderId);
      
      toast.success("Receipt created successfully");
      setManualDialogOpen(false);
      setManualForm({
        merchantName: "",
        transactionDate: new Date(),
        totalAmount: "",
        vatAmount: "",
        vatRate: "",
        category: "",
        notes: ""
      });
      loadReceipts();
    } catch (error) {
      console.error("Error creating receipt:", error);
      toast.error("Failed to create receipt");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedReceipt) return;

    try {
      const vatRate = editForm.vatRate && editForm.vatRate !== "exempt" ? parseFloat(editForm.vatRate) : null;
      
      const updatedData = {
        ...selectedReceipt,
        merchantName: editForm.merchantName,
        transactionDate: editForm.transactionDate instanceof Date ? editForm.transactionDate.toISOString() : editForm.transactionDate,
        totalAmount: parseFloat(editForm.totalAmount),
        vatAmount: editForm.vatAmount ? parseFloat(editForm.vatAmount) : 0,
        vatRate: vatRate,
        netAmount: parseFloat(editForm.totalAmount) - (editForm.vatAmount ? parseFloat(editForm.vatAmount) : 0),
        category: editForm.category,
        notes: editForm.notes,
        updatedAt: new Date().toISOString()
      };
      
      // Update file in Drive
      await uploadFile(
        `${selectedReceipt.id}.json`,
        JSON.stringify(updatedData, null, 2),
        'application/json',
        null,
        selectedReceipt.fileId
      );
      
      toast.success("Receipt updated");
      setEditDialogOpen(false);
      loadReceipts();
    } catch (error) {
      console.error("Error updating receipt:", error);
      toast.error("Failed to update receipt");
    }
  };

  const handleDelete = async (receipt) => {
    if (!confirm("Are you sure you want to delete this receipt?")) return;

    try {
      // Delete JSON file
      await deleteFile(receipt.fileId);
      
      // Delete image if exists
      if (receipt.imageFileId) {
        await deleteFile(receipt.imageFileId);
      }
      
      toast.success("Receipt deleted");
      loadReceipts();
    } catch (error) {
      console.error("Error deleting receipt:", error);
      toast.error("Failed to delete receipt");
    }
  };

  const openEdit = (receipt) => {
    setSelectedReceipt(receipt);
    setEditForm({
      merchantName: receipt.merchantName,
      totalAmount: receipt.totalAmount,
      vatAmount: receipt.vatAmount || 0,
      vatRate: receipt.vatRate !== null && receipt.vatRate !== undefined ? String(receipt.vatRate) : "exempt",
      category: receipt.category || "",
      notes: receipt.notes || "",
      transactionDate: new Date(receipt.transactionDate)
    });
    setEditDialogOpen(true);
  };

  const filteredReceipts = receipts.filter(r =>
    r.merchantName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const vatRateOptions = [
    { value: "20", label: "20% Standard" },
    { value: "5", label: "5% Reduced" },
    { value: "0", label: "0% Zero Rate" },
    { value: "exempt", label: "Exempt" }
  ];

  if (!initialized) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar activeItem="receipts" />
        <main className="lg:ml-64 pt-16 lg:pt-0 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar activeItem="receipts" />

      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-6 md:p-8 lg:p-12 max-w-7xl">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Receipts
              </h1>
              <p className="text-slate-600 mt-1">{receipts.length} receipts in Google Drive</p>
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="upload-receipt-btn"
                onClick={() => setUploadDialogOpen(true)}
                className="bg-slate-900 hover:bg-slate-800"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Receipt
              </Button>
              <Button
                data-testid="manual-receipt-btn"
                variant="outline"
                onClick={() => setManualDialogOpen(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Manual Entry
              </Button>
            </div>
          </div>

          {/* Search */}
          <Card className="mb-6 bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="search-receipts"
                  placeholder="Search by merchant..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </CardContent>
          </Card>

          {/* Receipts Table */}
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : filteredReceipts.length === 0 ? (
                <div className="text-center py-20">
                  <Receipt className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">No receipts found</p>
                  <Button onClick={() => setUploadDialogOpen(true)}>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Your First Receipt
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider border-b border-slate-200">
                        <th className="py-3 px-4 text-left font-medium">Date</th>
                        <th className="py-3 px-4 text-left font-medium">Merchant</th>
                        <th className="py-3 px-4 text-left font-medium">Category</th>
                        <th className="py-3 px-4 text-right font-medium">Total</th>
                        {vatEnabled && <th className="py-3 px-4 text-right font-medium">VAT</th>}
                        <th className="py-3 px-4 text-center font-medium">Status</th>
                        <th className="py-3 px-4 text-center font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredReceipts.map((receipt, index) => (
                        <tr
                          key={receipt.id}
                          data-testid={`receipt-row-${index}`}
                          className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                        >
                          <td className="py-3 px-4 text-sm text-slate-900">
                            {receipt.transactionDate?.slice(0, 10)}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-900 font-medium">
                            {receipt.merchantName}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {receipt.category || "-"}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-900 text-right font-mono">
                            £{(receipt.totalAmount || 0).toFixed(2)}
                          </td>
                          {vatEnabled && (
                            <td className="py-3 px-4 text-sm text-slate-900 text-right font-mono">
                              £{(receipt.vatAmount || 0).toFixed(2)}
                              {receipt.vatRate && (
                                <span className="text-slate-400 text-xs ml-1">({receipt.vatRate}%)</span>
                              )}
                            </td>
                          )}
                          <td className="py-3 px-4 text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              receipt.isMatched
                                ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                : "bg-amber-50 text-amber-700 border border-amber-200"
                            }`}>
                              {receipt.isMatched ? (
                                <>
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Matched
                                </>
                              ) : (
                                <>
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Unmatched
                                </>
                              )}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                data-testid={`edit-receipt-${index}`}
                                variant="ghost"
                                size="sm"
                                onClick={() => openEdit(receipt)}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              {isAdmin && (
                                <Button
                                  data-testid={`delete-receipt-${index}`}
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDelete(receipt)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Upload Receipt</DialogTitle>
          </DialogHeader>
          <div
            className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center cursor-pointer hover:border-slate-400 transition-colors"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              const file = e.dataTransfer.files?.[0];
              if (file) handleFileUpload(file);
            }}
          >
            {uploading ? (
              <Loader2 className="w-8 h-8 text-blue-600 mx-auto animate-spin" />
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">Drag & drop or click to upload</p>
                <p className="text-sm text-slate-400 mt-2">JPEG, PNG, GIF, WebP (max 10MB)</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handleFileUpload(file);
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Add Receipt Manually</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="merchant">Merchant Name *</Label>
              <Input
                id="merchant"
                data-testid="manual-merchant"
                value={manualForm.merchantName}
                onChange={(e) => setManualForm({ ...manualForm, merchantName: e.target.value })}
                placeholder="e.g., Tesco, Amazon"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" data-testid="manual-date" className="justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(manualForm.transactionDate, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={manualForm.transactionDate}
                      onSelect={(date) => date && setManualForm({ ...manualForm, transactionDate: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  data-testid="manual-category"
                  value={manualForm.category}
                  onChange={(e) => setManualForm({ ...manualForm, category: e.target.value })}
                  placeholder="e.g., Office Supplies"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="total">Total Amount *</Label>
                <Input
                  id="total"
                  data-testid="manual-total"
                  type="number"
                  step="0.01"
                  value={manualForm.totalAmount}
                  onChange={(e) => setManualForm({ ...manualForm, totalAmount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              {vatEnabled && (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="vat">VAT Amount</Label>
                    <Input
                      id="vat"
                      data-testid="manual-vat"
                      type="number"
                      step="0.01"
                      value={manualForm.vatAmount}
                      onChange={(e) => setManualForm({ ...manualForm, vatAmount: e.target.value })}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>VAT Rate</Label>
                    <Select
                      value={manualForm.vatRate}
                      onValueChange={(value) => setManualForm({ ...manualForm, vatRate: value })}
                    >
                      <SelectTrigger data-testid="manual-vat-rate">
                        <SelectValue placeholder="Rate" />
                      </SelectTrigger>
                      <SelectContent>
                        {vatRateOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                data-testid="manual-notes"
                value={manualForm.notes}
                onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                placeholder="Optional notes..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialogOpen(false)}>Cancel</Button>
            <Button data-testid="manual-submit" onClick={handleManualCreate} disabled={uploading}>
              {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Create Receipt
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Receipt Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Edit Receipt</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-merchant">Merchant Name</Label>
              <Input
                id="edit-merchant"
                data-testid="edit-merchant"
                value={editForm.merchantName || ""}
                onChange={(e) => setEditForm({ ...editForm, merchantName: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editForm.transactionDate && format(editForm.transactionDate, "PPP")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={editForm.transactionDate}
                      onSelect={(date) => date && setEditForm({ ...editForm, transactionDate: date })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-category">Category</Label>
                <Input
                  id="edit-category"
                  data-testid="edit-category"
                  value={editForm.category || ""}
                  onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-total">Total Amount</Label>
                <Input
                  id="edit-total"
                  data-testid="edit-total"
                  type="number"
                  step="0.01"
                  value={editForm.totalAmount || ""}
                  onChange={(e) => setEditForm({ ...editForm, totalAmount: e.target.value })}
                />
              </div>
              {vatEnabled && (
                <>
                  <div className="grid gap-2">
                    <Label htmlFor="edit-vat">VAT Amount</Label>
                    <Input
                      id="edit-vat"
                      data-testid="edit-vat"
                      type="number"
                      step="0.01"
                      value={editForm.vatAmount || ""}
                      onChange={(e) => setEditForm({ ...editForm, vatAmount: e.target.value })}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label>VAT Rate</Label>
                    <Select
                      value={String(editForm.vatRate || "exempt")}
                      onValueChange={(value) => setEditForm({ ...editForm, vatRate: value })}
                    >
                      <SelectTrigger data-testid="edit-vat-rate">
                        <SelectValue placeholder="Rate" />
                      </SelectTrigger>
                      <SelectContent>
                        {vatRateOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-notes">Notes</Label>
              <Input
                id="edit-notes"
                data-testid="edit-notes"
                value={editForm.notes || ""}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button data-testid="edit-submit" onClick={handleEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
